import { useState, useCallback } from 'react';
import { track } from '@/lib/analytics';
import { ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { completeOnboarding } from '@/services/paymentService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface OnboardingViewProps {
  onComplete: () => void;
}

const ROLES = [
  { value: 'makler', label: 'Immobilienmakler' },
  { value: 'stager', label: 'Home Stager' },
  { value: 'hausverwaltung', label: 'Hausverwaltung' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const DEFAULT_ROLE = 'sonstiges';

export const OnboardingView: React.FC<OnboardingViewProps> = ({ onComplete }) => {
  const { user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitOnboarding = useCallback(async (name: string, selectedRole: string) => {
    if (!user) return;
    setIsCompleting(true);
    setError(null);
    try {
      const success = await completeOnboarding({ fullName: name, role: selectedRole });
      if (success) {
        track('Onboarding Completed', { role: selectedRole, skipped: !name && selectedRole === DEFAULT_ROLE });
        onComplete();
      } else {
        setError('Onboarding konnte nicht abgeschlossen werden. Bitte versuche es erneut.');
        setIsCompleting(false);
      }
    } catch {
      setError('Ein unerwarteter Fehler ist aufgetreten.');
      setIsCompleting(false);
    }
  }, [user, onComplete]);

  const handleComplete = useCallback(() => {
    if (!fullName.trim() || !role) return;
    submitOnboarding(fullName.trim(), role);
  }, [fullName, role, submitOnboarding]);

  const handleSkip = useCallback(() => {
    submitOnboarding('', DEFAULT_ROLE);
  }, [submitOnboarding]);

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !isCompleting) handleSkip(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="items-center gap-2">
          <img src="/logo.png" alt="estage.ai" className="w-8 h-8" />
          <DialogTitle className="text-xl">Willkommen bei estage.ai!</DialogTitle>
          <DialogDescription>Lass uns dein Profil einrichten.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="onboard-name">Dein Name</Label>
            <Input
              id="onboard-name"
              placeholder="Max Mustermann"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && fullName.trim() && role) handleComplete(); }}
            />
          </div>

          <div className="space-y-2">
            <Label>Deine Rolle</Label>
            <div className="grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setRole(r.value)}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                    role === r.value
                      ? 'bg-primary/20 border-primary text-primary'
                      : 'bg-background border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Damit wir die App auf deinen Workflow anpassen können.</p>
          </div>
        </div>

        {error && (
          <Alert className="border-red-500/30 bg-red-950/20">
            <AlertDescription className="text-red-300 text-sm">{error}</AlertDescription>
          </Alert>
        )}

        <Button
          onClick={handleComplete}
          disabled={!fullName.trim() || !role || isCompleting}
          className="w-full bg-primary hover:bg-primary/90 text-white"
        >
          {isCompleting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ChevronRight className="ml-2 h-4 w-4" />
          )}
          Loslegen
        </Button>
      </DialogContent>
    </Dialog>
  );
};
